import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { useAuth } from "./auth";

const Ctx = createContext(null);

/**
 * Provides tier + groups info app-wide so BottomNav and other components
 * don't each re-fetch /billing/status and /groups.
 */
export function MembershipProvider({ children }) {
  const { user } = useAuth();
  const [tier, setTier] = useState(null);          // "basic" | "plus" | "elite" | null
  const [isNode, setIsNode] = useState(false);
  const [groups, setGroups] = useState([]);        // current user's accessible groups
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) { setLoaded(true); return; }
    try {
      const b = await api.get("/billing/status");
      setTier(b.data?.tier || null);
      setIsNode(!!b.data?.is_node);
    } catch {}
    try {
      const g = await api.get("/groups");
      setGroups(g.data || []);
    } catch {
      setGroups([]);
    } finally { setLoaded(true); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh groups every 30s so live/scheduled state changes show up in nav
  useEffect(() => {
    if (!user) return;
    const t = setInterval(async () => {
      try { const g = await api.get("/groups"); setGroups(g.data || []); } catch {}
    }, 30000);
    return () => clearInterval(t);
  }, [user]);

  const isEliteMaster = !isNode && tier === "elite";
  const nodeHasGroups = isNode && groups.length > 0;

  return (
    <Ctx.Provider value={{ tier, isNode, isEliteMaster, groups, nodeHasGroups, loaded, refreshMembership: refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export const useMembership = () => useContext(Ctx) || {
  tier: null, isNode: false, isEliteMaster: false, groups: [], nodeHasGroups: false, loaded: false, refreshMembership: () => {},
};
